import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Configure axios to send credentials and handle errors
axios.defaults.withCredentials = true;

// ============== 401 INTERCEPTOR (auto-refresh + logout) ==============
// Single in-flight refresh promise so concurrent 401s share one refresh call.
let refreshPromise = null;
// Callback the AuthProvider registers to clear its user state on terminal failure.
let onUnauthorizedHandler = null;

axios.interceptors.response.use(
  response => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || '';

    // Do not intercept auth endpoints themselves to avoid infinite loops.
    const isAuthEndpoint =
      url.includes('/auth/login') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/logout') ||
      url.includes('/auth/register');

    if (status === 401 && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${API}/auth/refresh`, {}, { withCredentials: true })
            .finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        return axios(original);
      } catch (refreshErr) {
        if (onUnauthorizedHandler) onUnauthorizedHandler();
        return Promise.reject(refreshErr);
      }
    }

    if (status === 401 && isAuthEndpoint && onUnauthorizedHandler && !url.includes('/auth/login')) {
      onUnauthorizedHandler();
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper to format API error
const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);

  // Register a global handler so the axios 401 interceptor (defined outside React)
  // can sign the user out when refresh fails.
  useEffect(() => {
    onUnauthorizedHandler = () => setUser(false);
    return () => { onUnauthorizedHandler = null; };
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, recaptchaToken) => {
    try {
      console.log('Attempting login to:', `${API}/auth/login`);
      const payload = { email, password };
      if (recaptchaToken) payload.recaptchaToken = recaptchaToken;
      const { data } = await axios.post(`${API}/auth/login`, payload, { withCredentials: true });
      console.log('Login successful:', data);
      setUser(data);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Network error. Please check your connection.';
      return { 
        success: false, 
        error: formatApiErrorDetail(errorMessage)
      };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { name, email, password }, { withCredentials: true });
      setUser(data);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(false);
  };

  const refreshToken = async () => {
    try {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      return true;
    } catch (error) {
      setUser(false);
      return false;
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API}/auth/change-password`, { 
        current_password: currentPassword, 
        new_password: newPassword 
      }, { withCredentials: true });
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const hasRole = (...roles) => {
    return user && roles.includes(user.role);
  };

  const isSuperAdmin = () => hasRole('super_admin');
  const isAdmin = () => hasRole('super_admin', 'admin');

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshToken,
    changePassword,
    checkAuth,
    hasRole,
    isSuperAdmin,
    isAdmin,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
